// PEE™ — Personal Execution Engine.
// The intelligence layer operating inside L∞seEnd™.
// This module owns every user-facing string that names the engine, so the
// consumer/enterprise copy modes stay consistent across the interface.

import { formatLongDate, isOverdue } from "./format";
import type { Loop, LoopStatus } from "./types";

export type CopyMode = "consumer" | "enterprise";
export type TriggerSensitivity = "quiet" | "balanced" | "proactive";

/** Screen readers should say the letters, not the word. */
export const PEE_ARIA = "P-E-E, Personal Execution Engine";
export const PEE_FULL_NAME = "Personal Execution Engine";

/** Official product category descriptor for L∞seEnd™ itself. */
export const SYSTEM_DESCRIPTOR = "Personal Execution System";

/** Accessible reading of the full brand lockup. */
export const LOCKUP_ARIA =
  "L-Infinity-se-End, Personal Execution System, powered by P-E-E, Personal Execution Engine.";

/** Consumer surfaces use the short mark; enterprise surfaces expand it first. */
export function engineName(mode: CopyMode = "consumer"): string {
  return mode === "enterprise" ? "Personal Execution Engine (PEE™)" : "PEE™";
}

export const PEE_EXPLANATION =
  "PEE™ is the Personal Execution Engine inside L∞seEnd™, your Personal Execution System. It keeps track of unfinished matters, watches for expected outcomes and surfaces the next useful action.";

export const PEE_SHORT_EXPLANATION =
  "L∞seEnd™ helps you capture, monitor and resolve unfinished matters. PEE™ is the intelligence that determines when action becomes useful.";

export const PEE_SETTINGS_EXPLANATION =
  "PEE™ is the Personal Execution Engine inside L∞seEnd™. These settings control when the engine analyzes, recommends, and surfaces unresolved matters.";

/** Tooltip/popover copy shown from the sidebar "Powered by PEE™" line. */
export const PEE_SIDEBAR_NOTE = "The intelligence inside the L∞seEnd™ Personal Execution System.";

export const SYSTEM_EXPLANATION =
  "L∞seEnd™ is a Personal Execution System for capturing, monitoring and resolving unfinished matters.";

export const ENGINE_EXPLANATION =
  "PEE™ is the Personal Execution Engine inside L∞seEnd™. It interprets Loops, monitors expected outcomes, detects meaningful changes, recommends useful actions and requests verification before closure.";

export const PEE_CAPABILITIES = [
  "Recognize unfinished matters",
  "Structure them as Loops",
  "Monitor expected outcomes",
  "Detect when something changes",
  "Determine when action becomes useful",
  "Identify dependencies",
  "Request evidence",
  "Verify resolution",
] as const;

/** Enterprise-facing vocabulary, kept ready for a future technical surface. */
export const ENTERPRISE_TERMS = [
  "Personal Execution Engine architecture",
  "Resolution-state monitoring",
  "Evidence-based closure",
  "Dependency-aware prioritization",
  "Authorized execution",
  "Outcome verification",
] as const;

/* ------------------------------------------------------------ insights */

export interface PeeInsightCopy {
  headline: string;
  detail: string;
}

/** What the engine is monitoring for a waiting Loop. */
export function watchingNote(loop: Loop): PeeInsightCopy | null {
  if (loop.status !== "waiting" && loop.status !== "blocked") return null;
  const who = loop.counterparty ? `${loop.counterparty}'s response` : loop.expectedEvent;
  if (loop.expectedDate) {
    return {
      headline: "PEE™ is watching for:",
      detail: `${who} by ${formatLongDate(loop.expectedDate)}.`,
    };
  }
  return {
    headline: "PEE™ recommends waiting.",
    detail: "No action is useful yet — the next move belongs to someone else.",
  };
}

/** One restrained insight for a Loop, or null when nothing is worth saying. */
export function loopInsight(loop: Loop, all: Loop[]): PeeInsightCopy | null {
  const unblocks = all.filter((l) =>
    l.dependencies.some((d) => d.loopId === loop.id && d.relation === "blocked_by"),
  );
  if (loop.status === "ready_to_verify") {
    return {
      headline: "PEE™ thinks this may already be resolved.",
      detail: "The action looks complete, but the outcome still needs confirmation.",
    };
  }
  if (isOverdue(loop)) {
    return {
      headline: "PEE™ noticed that the expected response never arrived.",
      detail: `${loop.counterparty ?? "The other party"} was expected to respond by ${formatLongDate(
        loop.expectedDate ?? null,
      )}.`,
    };
  }
  if (loop.status === "blocked") {
    const blocker = loop.dependencies.find((d) => d.relation === "blocked_by");
    return {
      headline: "PEE™ found a dependency blocking this Loop.",
      detail: blocker ? `${blocker.note}.` : "Something else has to happen before this can move.",
    };
  }
  if (unblocks.length) {
    return {
      headline: "PEE™ caught this before it became overdue.",
      detail: `Resolving it will unblock ${unblocks.length} other Loop${unblocks.length === 1 ? "" : "s"}.`,
    };
  }
  return watchingNote(loop);
}

/** Plain-language reason a Loop appears in the Resolution Queue. */
export function surfacedBecause(loop: Loop, all: Loop[], explanation: string): string {
  const unblocks = all.filter((l) =>
    l.dependencies.some((d) => d.loopId === loop.id && d.relation === "blocked_by"),
  );
  if (isOverdue(loop)) return "PEE™ surfaced this because the expected response is overdue.";
  if (unblocks.length)
    return `PEE™ surfaced this because resolving it will unblock ${unblocks.length} other Loop${
      unblocks.length === 1 ? "" : "s"
    }.`;
  if (loop.status === "ready_to_verify")
    return "PEE™ surfaced this because the outcome still needs confirmation.";
  if (loop.status === "blocked") return "PEE™ recommends waiting until the blocking input arrives.";
  return explanation;
}

/* ------------------------------------------------------------- summary */

export interface EngineTally {
  actionNeeded: number;
  waiting: number;
  readyToVerify: number;
  total: number;
}

export function engineTally(counts: Record<LoopStatus, number>): EngineTally {
  const actionNeeded = counts.action_needed;
  const waiting = counts.waiting + counts.blocked;
  const readyToVerify = counts.ready_to_verify;
  return { actionNeeded, waiting, readyToVerify, total: actionNeeded + waiting + readyToVerify };
}

/** "PEE™ found 2 Loops needing action, 3 waiting on others, and 1 ready to verify." */
export function tallySentence(tally: EngineTally): string {
  const parts: string[] = [];
  if (tally.actionNeeded)
    parts.push(`${tally.actionNeeded} Loop${tally.actionNeeded === 1 ? "" : "s"} needing action`);
  if (tally.waiting) parts.push(`${tally.waiting} waiting on others`);
  if (tally.readyToVerify) parts.push(`${tally.readyToVerify} ready to verify`);
  if (!parts.length) return "PEE™ found nothing that needs you right now.";
  const list =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(", ")}${parts.length > 2 ? "," : ""} and ${parts[parts.length - 1]}`;
  return `PEE™ found ${list}.`;
}

/* ------------------------------------------------- notification samples */

export interface NotificationSample {
  title: string;
  body: string;
}

/** Prototype notification copy. Calm, useful, never a bathroom joke. */
export const NOTIFICATION_SAMPLES: NotificationSample[] = [
  { title: "PEE™ caught something.", body: "Susan's response was expected yesterday." },
  {
    title: "PEE™ found a Loop that may be resolved.",
    body: "Your invoice payment may have been confirmed.",
  },
  {
    title: "PEE™ recommends waiting.",
    body: "No action is useful until the vendor quote arrives.",
  },
  {
    title: "PEE™ found three things waiting on other people.",
    body: "Open the Resolution Queue to review them.",
  },
];
